import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { MemberIdentity } from '../members/MemberIdentity'
import {
  fullName,
  groupLabel,
  sameGenderCategories,
  type CompetitionCategory,
  type CompetitionEntry,
  type CompetitionSession,
} from './types'

interface Props {
  /** The competitor being moved; null while the modal is closed. */
  entry: CompetitionEntry | null
  categories: CompetitionCategory[]
  sessions: CompetitionSession[]
  onClose: () => void
  onMoved: () => void
}

/** The refusals staff can actually run into; anything else is a plain failure. */
const ERROR_KEYS: Record<string, string> = {
  CATEGORY_FULL: 'competitions.moveCategoryFull',
  SESSION_FULL: 'competitions.moveGroupFull',
  CATEGORY_WRONG_AGE: 'competitions.moveWrongAge',
  PROFILE_DOB_REQUIRED: 'competitions.moveDobRequired',
}

/**
 * Moves a competitor to another category of the same gender, another time
 * slot, or both. The entry is kept, so their results and their confirmation at
 * the desk come along, which withdrawing and rejoining would throw away.
 */
export function MoveCompetitorModal({ entry, categories, sessions, onClose, onMoved }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { primaryColor } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [categoryId, setCategoryId] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!entry) return
    setCategoryId(entry.category_id)
    setSessionId(entry.session_id ?? '')
    setError(null)
  }, [entry])

  const categoryOptions = entry ? sameGenderCategories(entry, categories) : []
  const categoryChanged = !!entry && categoryId !== entry.category_id
  const sessionChanged = !!entry && sessionId !== (entry.session_id ?? '')

  async function move() {
    if (!entry) return
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competition-entries/${entry.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // Only what changed, so an untouched field is never re-checked for seats.
        body: JSON.stringify({
          ...(categoryChanged ? { category_id: categoryId } : {}),
          ...(sessionChanged ? { session_id: sessionId } : {}),
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { code?: string } | null
        setError(t(ERROR_KEYS[body?.code ?? ''] ?? 'competitions.errorSaveFailed'))
        return
      }
      onMoved()
      onClose()
    } catch {
      setError(t('competitions.errorSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen={entry !== null} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{t('competitions.moveCompetitorTitle')}</ModalHeader>
        <ModalBody className="gap-3">
          {entry && (
            <MemberIdentity
              userId={entry.user_id}
              name={fullName(entry.profile)}
              nickname={entry.profile?.nickname}
              email={entry.profile?.email}
              avatarExt={entry.profile?.avatar_ext}
              avatarUpdatedAt={entry.profile?.avatar_updated_at}
              primaryColor={primaryColor}
              size="sm"
            />
          )}
          <p className="text-sm text-foreground/60">{t('competitions.moveCompetitorHint')}</p>
          {categoryOptions.length > 1 && (
            <Select
              label={t('competitions.colCategory')}
              selectedKeys={categoryId ? [categoryId] : []}
              onSelectionChange={(keys) => {
                // Clicking the selected item again hands back an empty set.
                const val = Array.from(keys)[0]
                if (val) setCategoryId(String(val))
              }}
            >
              {categoryOptions.map((c) => (
                <SelectItem key={c.id} textValue={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </Select>
          )}
          {sessions.length > 0 && (
            <Select
              label={t('competitions.colGroup')}
              selectedKeys={sessionId ? [sessionId] : []}
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0]
                if (val) setSessionId(String(val))
              }}
            >
              {sessions.map((s) => (
                <SelectItem key={s.id} textValue={groupLabel(s)}>
                  {groupLabel(s)}
                </SelectItem>
              ))}
            </Select>
          )}
          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            isLoading={saving}
            isDisabled={!categoryChanged && !sessionChanged}
            onPress={() => void move()}
          >
            {t('competitions.moveCompetitor')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
